import { Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getMessages } from '../../api/pushMessage.api';
import ErrorResponse from '../../components/ErrorResponse';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import ConfirmModal from '../../components/modals/ConfirmModal';
import Pagination from '../../components/Pagination';
import SearchBar from '../../components/sms/SearchBar';
import useAxios from '../../hooks/useAxios';
import useUtil from '../../hooks/useUtil';
import config from '../../lib/config';
import { IGetMessages, IMessage } from '../../models/pushMessage.model';
import { RootState } from '../../store/rootReducer';

const SmsMember: FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { isCampusDeactived } = useUtil();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetMessages>();
  const [page, setPage] = useState(1);
  const [param, setParam] = useState<any>({});
  const [srtDate, setSrtDate] = useState(
    moment().subtract(30, 'days').format('YYYYMMDD'),
  );
  const [endDate, setEndDate] = useState(moment().format('YYYYMMDD'));
  const [sendType, setSendType] = useState('ALL');
  const [sendTimeDiv, setSendTimeDiv] = useState('ALL');
  const [memberName, setMemberName] = useState('');
  const [showAlertVisible, setShowAlertVisible] = useState(false);
  const [campusDeactived, setCampusDeactived] = useState(false);

  useEffect(() => {
    setCampusDeactived(isCampusDeactived());
  }, [isCampusDeactived]);

  useEffect(() => {
    if (currentCampus) {
      // query to state
      const _param: any = { pageNo: page, perPage: config.pageSize };
      _param.campusCode = currentCampus;

      if (query.page) {
        _param.pageNo = Number(query.page);
        setPage(_param.pageNo);
      }
      if (query.srtDate) {
        _param.srtDate = String(query.srtDate);
        setSrtDate(_param.srtDate);
      }
      if (query.endDate) {
        _param.endDate = String(query.endDate);
        setEndDate(_param.endDate);
      }
      if (query.sendType) {
        _param.sendType = String(query.sendType);
        setSendType(_param.sendType);
      }
      if (query.sendTimeDiv) {
        _param.sendTimeDiv = String(query.sendTimeDiv);
        setSendTimeDiv(_param.sendTimeDiv);
      }
      if (query.memberName) {
        _param.memberName = String(query.memberName);
        setMemberName(_param.memberName);
      }
      if (query.srtDate && query.endDate) {
        _param.srtDate = String(query.srtDate);
        _param.endDate = String(query.endDate);
        setSrtDate(_param.srtDate);
        setEndDate(_param.endDate);
      } else if (srtDate && endDate) {
        _param.srtDate = srtDate;
        _param.endDate = endDate;
      }
      doGetMessages(_param);
      _param.page = _param.pageNo;
      delete _param.pageNo;
      delete _param.perPage;
      setParam(_param);
    }
  }, [location, currentCampus]);

  const doGetMessages = (_param?: any) => {
    execute(
      getMessages(_param || param || { pageNo: 1, perPage: config.pageSize }),
    );
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (srtDate && endDate) {
      param.srtDate = srtDate;
      param.endDate = endDate;
    }
    if (sendType !== 'ALL') param.sendType = sendType;
    if (sendTimeDiv !== 'ALL') param.sendTimeDiv = sendTimeDiv;
    if (memberName) param.memberName = memberName;

    setParam(param);
    history.push(`/sms/member?${QueryString.stringify(param)}`);
  };

  const onSend = () => history.push('/sms/member/send');

  const columns: TableColumnsType<IMessage> = [
    {
      title: t('sms.list.column15'),
      dataIndex: 'sendDateTime',
      align: 'center',
      width: '10%',
      render: (value) => moment(value).format('YYYY.MM.DD HH:mm:ss'),
    },
    {
      title: t('sms.list.column2'),
      dataIndex: 'sendType',
      align: 'center',
      width: '6%',
      render: (value) => {
        switch (value) {
          case 'GE':
            return t('student.student.option20');
          case 'AD':
            return t('student.student.option21');
          default:
            return '-';
        }
      },
    },
    {
      title: t('sms.list.column5'),
      dataIndex: 'memberName',
      align: 'center',
      width: '10%',
      render: (value, record) =>
        i18n.language === 'ko' ? value : record.memberEngName,
    },
    {
      title: t('common.column.title'),
      dataIndex: 'title',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('common.column.contents'),
      dataIndex: 'content',
      align: 'center',
      ellipsis: true,
      render: value => {
        return <div className="text-left ant-table-cell-ellipsis">{value}</div>
      }
    },
    {
      title: t('sms.list.column16'),
      dataIndex: 'sendTimeDiv',
      align: 'center',
      width: '10%',
      render: (value) => {
        switch (value) {
          case 'IM':
            return t('student.student.option22');
          case 'RE':
            return t('student.student.option23');
          case 'CN':
            return t('sms.list.option1');
          default:
            return '-';
        }
      },
    },
    {
      title: t('sms.list.column17'),
      dataIndex: 'sendDateTime',
      align: 'center',
      width: '10%',
      render: (value, record) => {
        if (record.sendTimeDiv === 'RE') {
          return moment(value).format('YYYY.MM.DD HH:mm:ss');
        } else {
          return '-';
        }
      },
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-sms-big.svg"
      parentMenu={t('common.menu.text')}
      currentMenu={t('sms.list.title2')}
      onlyTitle={true}
    />	
    <SearchBar
        startDate={srtDate}
        setStartDate={setSrtDate}
        endDate={endDate}
        setEndDate={setEndDate}
        sendType={sendType}
        setSendType={setSendType}
        sendTimeDiv={sendTimeDiv}
        setSendTimeDiv={setSendTimeDiv}
        onSearch={onSearch}
        onSend={onSend}
        memberName={memberName}
        setMemberName={setMemberName}
        campusDeactived={campusDeactived}
      />

    <div className="content scrollbar">
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      
      <div className="col-frame">
        <div className='comm-bx__between'>
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{res?.total || 0}</b>
            {t('common.text.unit')}
          </div>
        </div>
        <div className='comm-table__area'>
          <Table
            columns={columns}
            dataSource={res?.data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if (record.sendTimeDiv === 'CN') {
                    setShowAlertVisible(true);
                  } else {
                    history.push(
                      `/sms/member/${record.smsSendCode}${location.search}`,
                    );
                  }
                },
              };
            }}
            rowClassName="pointer"
            rowKey="smsSendCode"
          />
        </div>
        <div className="comm-bx__between">
          <div className="common-paging center">
            <Pagination
              count={res?.total || 0}
              page={page}
              param={param || {}}
              path="/sms/member"
            />
          </div>
        </div>
      </div>

      {showAlertVisible && (
        <ConfirmModal
          visible={showAlertVisible}
          setVisible={setShowAlertVisible}
          onConfirm={() => setShowAlertVisible(false)}
          titleText={t('sms.list.title3')}
          messageText={t('sms.list.message1')}
          confirmText={t('common.button.confirm')}
        />
      )}
    </div>
    </>
  );
};

export default SmsMember;
